import type { CSSProperties } from 'react';
import { useImageUpload } from '../utils/use-image-upload';
import styles from '../template-styles.module.css';
import type { MediaWidgetSize, MediaWidgetProps } from './widget-types';

export type ImageWidgetSize = MediaWidgetSize;

export interface ImageWidgetProps extends MediaWidgetProps {
  width?: number;
  borderStyle?: CSSProperties['borderStyle'];
  borderColor?: string;
  alt?: string;
  url?: string;
}

export default function ImageWidget({
  src,
  setImage,
  size = 'original',
  width,
  height,
  scale = 100,
  align = 'center',
  background,
  border,
  borderStyle,
  borderColor,
  borderRadius,
  padding,
  filter,
  alt,
  url,
}: ImageWidgetProps) {
  const [loading, upload] = useImageUpload(setImage);

  const isFill = size === 'fill';
  const isScale = size === 'scale';
  const resolvedBorder = border ? `${border} ${borderStyle ?? 'solid'} ${borderColor ?? 'transparent'}` : undefined;

  // the link is only a real navigation target in the sent email; inside the editor it would just
  // steal the click away from the app, so it's neutralized there the same way ButtonWidget does
  const wrapInLink = (children: React.ReactNode) =>
    url ? (
      <a href={url} onClick={(e) => e.preventDefault()} style={{ display: 'block', textDecoration: 'none' }}>
        {children}
      </a>
    ) : (
      children
    );

  const interactiveProps = setImage
    ? {
        role: 'button' as const,
        tabIndex: 0,
        'aria-label': src ? 'Change image' : 'Add image',
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            upload();
          }
        },
      }
    : undefined;

  return (
    <table
      role="presentation"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      style={{ background, fontSize: 0, lineHeight: '0' }}
    >
      <tbody>
        <tr>
          <td align={align} style={{ padding }}>
            {src ? (
              wrapInLink(
                <div
                  onClick={upload}
                  data-cont={setImage ? 'img' : undefined}
                  className={loading ? styles.imgLoading : undefined}
                  style={
                    isScale
                      ? { width: `${scale}%` }
                      : isFill
                        ? { width: '100%' }
                        : { width: width ?? '100%', height: height ?? 'auto' }
                  }
                  {...interactiveProps}
                >
                  <img
                    height={isFill ? height : undefined}
                    src={src}
                    style={{
                      display: 'block',
                      width: isFill || isScale ? '100%' : (width ?? '100%'),
                      minWidth: isFill || isScale ? 0 : undefined,
                      maxWidth: '100%',
                      height: isFill ? height : isScale ? 'auto' : (height ?? 'auto'),
                      objectFit: 'cover',
                      border: resolvedBorder,
                      borderRadius,
                      filter,
                    }}
                    alt={alt ?? ''}
                  />
                </div>
              )
            ) : (
              <div
                onClick={upload}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: setImage ? 'pointer' : 'default',
                  width: isFill || isScale ? '100%' : (width ?? '100%'),
                  height: isScale ? 120 : (height ?? 120),
                  border: '1px dashed currentColor',
                  borderRadius,
                  fontSize: '13px',
                  fontFamily: 'Arial, sans-serif',
                  opacity: 0.5,
                }}
                {...interactiveProps}
              >
                Click to add image
              </div>
            )}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
